/**
 * 差出人情報（開発者単位 = すべてのゲームで共通）: 正式な氏名 / 法人名・住所（私書箱可）・問い合わせ先メール。
 * GET / PUT …/mail/sender（Core の案）。
 *
 * WHY このカードを本文より上に置くか: 揃うまで送れない（サーバーは 400 sender_incomplete）。フッターに必ず入る法律の表示
 *   （CAN-SPAM の物理住所・特定電子メール法の送信者の表示）なので、先に埋めてもらう。
 * 揃っていれば中身を短く見せて「変更」で開く（毎回フォームを見せると、書く欄が下に押し出される）。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { ErrorState, Spinner } from '../../components/ui';
import type { Resource } from '../../hooks';
import { errorText, useT, type MessageKey } from '../../i18n';
import type { MailSender } from '../../mailApi';
import { MAIL_LIMITS, validateSender, type MailSenderInput, type SenderField, type SenderProblem } from '../../mailLogic';

const FIELD_KEYS: Record<SenderField, { label: MessageKey; hint: MessageKey }> = {
  legalName: { label: 'mail.sender.legalName', hint: 'mail.sender.legalNameHint' },
  postalAddress: { label: 'mail.sender.postalAddress', hint: 'mail.sender.postalAddressHint' },
  contactEmail: { label: 'mail.sender.contactEmail', hint: 'mail.sender.contactEmailHint' }
};
const PROBLEM_KEYS: Record<SenderProblem, MessageKey> = {
  required: 'mail.problem.required',
  tooLong: 'mail.problem.tooLong',
  email: 'mail.problem.email'
};

const toInput = (s: MailSender | null): MailSenderInput => ({
  legalName: s?.legalName ?? '',
  postalAddress: s?.postalAddress ?? '',
  contactEmail: s?.contactEmail ?? ''
});

export function SenderCard({
  mail, resource, absent, sender, missing
}: { mail: string; resource: Resource<unknown>; absent: boolean; sender: MailSender | null; missing: SenderField[] }) {
  const { t } = useT();
  // absent = まだ一度も保存していない（404）。エラーではなく空のフォームを出す
  const loaded = !!resource.data || absent;
  const complete = !!resource.data && missing.length === 0;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<MailSenderInput>(toInput(sender));
  const [problems, setProblems] = useState<Array<{ field: SenderField; problem: SenderProblem }>>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  // 読み込み直した値をフォームに入れる（編集中は上書きしない = 打っている途中の文字を消さない）
  useEffect(() => {
    if (!editing) setForm(toInput(sender));
  }, [sender, editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const issues = validateSender(form);
    setProblems(issues);
    setSaved(false);
    if (issues.length) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`${mail}/sender`, {
        method: 'PUT',
        body: { legalName: form.legalName.trim(), postalAddress: form.postalAddress.trim(), contactEmail: form.contactEmail.trim() }
      });
      setEditing(false);
      setSaved(true);
      resource.reload();
    } catch (e2) {
      setErr(e2 as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const problemOf = (f: SenderField) => problems.find((p) => p.field === f);
  const showForm = editing || (loaded && !complete);

  return (
    <section className="card mail-sender" id="mail-sender" aria-labelledby="mail-sender-title">
      <div className="card-head">
        <h2 className="card-title" id="mail-sender-title">
          {t('mail.sender.title')}
        </h2>
        {loaded && <span className={`pill ${complete ? 'pill-ok' : 'pill-warn'}`}>{complete ? t('mail.sender.complete') : t('mail.sender.incomplete')}</span>}
      </div>
      <p className="small muted">{t('mail.sender.why')}</p>

      {resource.loading && !resource.data && <Spinner />}
      {resource.error && !absent && <ErrorState error={resource.error} what={t('mail.sender.title')} onRetry={resource.reload} />}
      {saved && (
        <p className="alert alert-ok" role="status">
          {t('mail.sender.saved')}
        </p>
      )}

      {complete && !editing && sender && (
        <>
          <dl className="kv mail-sender-kv">
            <dt>{t('mail.sender.legalName')}</dt>
            <dd>{sender.legalName}</dd>
            <dt>{t('mail.sender.postalAddress')}</dt>
            <dd className="mail-pre">{sender.postalAddress}</dd>
            <dt>{t('mail.sender.contactEmail')}</dt>
            <dd>{sender.contactEmail}</dd>
          </dl>
          <div className="form-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
              {t('mail.sender.edit')}
            </button>
          </div>
        </>
      )}

      {showForm && (
        <form onSubmit={submit} noValidate>
          <p className="small muted">{t('mail.sender.shared')}</p>
          <div className="mail-fields">
            {(['legalName', 'postalAddress', 'contactEmail'] as const).map((f) => {
              const p = problemOf(f);
              const id = `mail-sender-${f}`;
              return (
                <label className="field" key={f} htmlFor={id}>
                  <span>
                    {t(FIELD_KEYS[f].label)} <small className="muted">{t(FIELD_KEYS[f].hint)}</small>
                  </span>
                  {f === 'postalAddress' ? (
                    <textarea
                      id={id}
                      className="textarea"
                      rows={3}
                      value={form[f]}
                      maxLength={MAIL_LIMITS[f]}
                      aria-invalid={!!p}
                      onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    />
                  ) : (
                    <input
                      id={id}
                      type={f === 'contactEmail' ? 'email' : 'text'}
                      autoComplete={f === 'contactEmail' ? 'email' : 'name'}
                      value={form[f]}
                      maxLength={MAIL_LIMITS[f]}
                      aria-invalid={!!p}
                      onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                    />
                  )}
                  {p && <small className="field-error">{t(PROBLEM_KEYS[p.problem], { max: MAIL_LIMITS[f] })}</small>}
                </label>
              );
            })}
          </div>
          {err && (
            <p className="alert alert-error" role="alert">
              {errorText(err)}
            </p>
          )}
          <div className="form-actions">
            {editing && complete && (
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </button>
            )}
            <button className="btn btn-primary" disabled={busy}>
              {busy ? t('common.working') : t('mail.sender.save')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
